import { useInfiniteExternalIntegrationActivities } from '@Queries/index';
import * as Api from '@ViewModels';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '@WebComponents/DataGrid';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from '@WebComponents/Popover';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../../../../../models/UiUtils';
import { Button, SelectButton } from '../../../../../../../web/components-v2/Button';
import { Calendar } from '../../../../../../../web/components-v2/Calendar';
import { ClipboardIcon } from '../../../../../../../web/components/svgs/icons/ClipboardIcon';
import { baseStyleSheet } from '../../../../../../../web/styles/styles';
import { styleSheet } from '../../styles';

const DATE_FORMAT = 'MM/DD';

const DATE_RANGE_PRESETS = [7, 14, 30, 60, 90] as const;

export const RecentActivityView = ({
	impersonationContext,
	integrationProvider,
}: {
	impersonationContext: Api.IImpersonationContext;
	integrationProvider: Api.IntegrationProvider;
}) => {
	const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined);
	const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

	// Draft states for popover (applied on OK click)
	const [draftDateRange, setDraftDateRange] = React.useState<DateRange | undefined>(undefined);

	const filterRequest: Api.IntegrationActivitiesFilterRequest = {
		startDate: dateRange?.from,
		endDate: dateRange?.to,
	};

	const isSearching = !!(dateRange?.from || dateRange?.to);

	const activitiesQuery = useInfiniteExternalIntegrationActivities({
		filterRequest,
		impersonationContext,
		integrationProvider,
		pageSize: 50,
	});

	const isLoadStats = (stats: Api.ProcessedStatsTypes): stats is Api.LoadProcessedStats => {
		return 'loaded' in stats;
	};

	const onPresetSelected = (days: number) => {
		const from = moment().subtract(days, 'days').startOf('day').toDate();
		const to = moment().endOf('day').toDate();
		setDraftDateRange({ from, to });
	};

	const onOkClick = () => {
		setDateRange(draftDateRange);
		setIsPopoverOpen(false);
	};

	const onCancelClick = () => {
		setDraftDateRange(dateRange);
		setIsPopoverOpen(false);
	};

	const onClearSearch = () => {
		setDateRange(undefined);
		setDraftDateRange(undefined);
	};

	const formatDateRange = () => {
		if (dateRange?.from && dateRange?.to) {
			return (
				<span>
					<span>{moment(dateRange.from).format(DATE_FORMAT)}</span>
					{' — '}
					<span>{moment(dateRange.to).format(DATE_FORMAT)}</span>
				</span>
			);
		}
		return 'Date Range';
	};

	// Handle query errors and show fallback message
	React.useEffect(() => {
		if (activitiesQuery.isError) {
			const error = activitiesQuery.error as any;
			const errorMessage =
				error?.systemMessage || error?.message || 'Failed to load activities. Please try again or clear filters.';
			toast.error(errorMessage);
			// Reset to no filters on error
			setDateRange(undefined);
		}
	}, [activitiesQuery.isError, activitiesQuery.error]);

	if (activitiesQuery.isPending) return <LoadingSpinner type='large' />;

	const items: Api.ProcessedStatsTypes[] =
		activitiesQuery.data?.pages?.flatMap((page: Api.IPagedCollection<Api.ProcessedStatsTypes>) => page.values) ?? [];

	const formatJobId = (jobId: string) => {
		if (!jobId || jobId.length <= 12) return jobId;
		const firstSix = jobId.slice(0, 6);
		const lastSix = jobId.slice(-6);
		return `${firstSix}...${lastSix}`;
	};

	const copyJobId = (jobId: string) => {
		copyToClipboard(jobId);
		toast.success('Job ID copied to clipboard');
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.filterContainer)}>
				<Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
					<PopoverTrigger asChild>
						<SelectButton>{formatDateRange()}</SelectButton>
					</PopoverTrigger>
					<PopoverContent align='start' side='bottom' styles={[styleSheet.popoverContent]}>
						<div className={css(styleSheet.dateRangeContainer)}>
							<div className={css(styleSheet.presetButtons)}>
								{DATE_RANGE_PRESETS.map(preset => (
									<Button
										key={`recent-activity-preset-${preset}`}
										onClick={() => onPresetSelected(preset)}
										size='sm'
										variant='secondary'
									>
										{`Last ${preset} days`}
									</Button>
								))}
							</div>
							<div className={css(styleSheet.calendarContainer)}>
								<Calendar
									mode='range'
									onSelect={setDraftDateRange}
									selected={draftDateRange}
									numberOfMonths={2}
									disabled={{ after: moment().toDate() }}
								/>
							</div>
						</div>
						<div className={css(styleSheet.buttonContainer)}>
							<Button onClick={onCancelClick} size='sm' variant='secondary'>
								Cancel
							</Button>
							<Button onClick={onOkClick} size='sm' variant='primary'>
								OK
							</Button>
						</div>
					</PopoverContent>
				</Popover>
				{isSearching && (
					<button className={css(styleSheet.clearSearch)} onClick={onClearSearch}>
						<span>Clear Search</span>
					</button>
				)}
			</div>

			<DataGrid styles={[styleSheet.tableOverrides]}>
				<DataGridColumns>
					<DataGridColumn sticky>Type</DataGridColumn>
					<DataGridColumn sticky>Job Id</DataGridColumn>
					<DataGridColumn sticky>Start Date</DataGridColumn>
					<DataGridColumn sticky>End Date</DataGridColumn>
					<DataGridColumn sticky>Duration</DataGridColumn>
					<DataGridColumn sticky>Total</DataGridColumn>
					<DataGridColumn sticky>Loaded</DataGridColumn>
					<DataGridColumn sticky>Archived</DataGridColumn>
					<DataGridColumn sticky>Skipped</DataGridColumn>
				</DataGridColumns>

				<DataGridRows>
					{!activitiesQuery.isPending &&
						items.map(item => {
							const start = item.start ? moment(item.start).format('MMM DD, hh:mm a') : '-';
							const end = item.end ? moment(item.end).format('MMM DD, hh:mm a') : '-';
							const duration = item.duration ? `${item.duration.substring(0, item.duration.length - 3)}ms` : '-';
							const loaded = isLoadStats(item) ? item.loaded : '-';
							const archived = isLoadStats(item) ? item.archived : '-';
							const skipped = isLoadStats(item) ? item.skipped : '-';
							return (
								<DataGridRow key={`row-${item.jobId}`}>
									<DataGridRowItem>{item._type.replace('ProcessedStats', '')}</DataGridRowItem>
									<DataGridRowItem>
										<button
											className={css(styleSheet.copyButton)}
											onClick={() => copyJobId(item.jobId)}
											title='Click to copy full Job ID'
										>
											{formatJobId(item.jobId)} <ClipboardIcon />
										</button>
									</DataGridRowItem>
									<DataGridRowItem>{start}</DataGridRowItem>
									<DataGridRowItem>{end}</DataGridRowItem>
									<DataGridRowItem>{duration}</DataGridRowItem>
									<DataGridRowItem>
										<h6 className={css(baseStyleSheet.truncateText, baseStyleSheet.m0)}>{item.total}</h6>
									</DataGridRowItem>
									<DataGridRowItem>{loaded}</DataGridRowItem>
									<DataGridRowItem>{archived}</DataGridRowItem>
									<DataGridRowItem>{skipped}</DataGridRowItem>
								</DataGridRow>
							);
						})}

					{activitiesQuery.isFetchingNextPage ? (
						<DataGridRow>
							<DataGridRowItem colSpan={9}>
								<LoadingSpinner type='large' />
							</DataGridRowItem>
						</DataGridRow>
					) : null}
				</DataGridRows>
			</DataGrid>

			{!activitiesQuery.isFetchingNextPage && activitiesQuery.hasNextPage ? (
				<Waypoint bottomOffset='-200px' onEnter={() => activitiesQuery.fetchNextPage()} />
			) : null}

			{activitiesQuery.isSuccess && items.length === 0 ? (
				<div className={css(baseStyleSheet.flexCenter, baseStyleSheet.mt4)}>
					<p className={css(styleSheet.emptyState)}>No activities found</p>
				</div>
			) : null}
		</div>
	);
};
